import { useState, useRef, useEffect } from 'react';
import { IconButton, TextField, CircularProgress, Tooltip, Snackbar, Alert } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import './InlineEditText.css';

interface InlineEditTextProps {
  text: string;
  onSave: (newText: string) => Promise<boolean>;
  disabled?: boolean;
  className?: string;
  /** Compact mode for table cells: single-line, auto-saves on Enter/blur, no action buttons. */
  compact?: boolean;
}

const InlineEditText = ({ text, onSave, disabled, className = '', compact = false }: InlineEditTextProps) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(text);
  const [optimisticText, setOptimisticText] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const inputRef = useRef<HTMLInputElement>(null);

  const displayText = optimisticText ?? text;

  useEffect(() => {
    if (!editing) setValue(displayText);
  }, [displayText, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const handleConfirm = async () => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === displayText) {
      setEditing(false);
      return;
    }

    setOptimisticText(trimmed);
    setSaving(true);
    setEditing(false);

    const success = await onSave(trimmed);
    setSaving(false);

    if (success) {
      setToast({ open: true, message: 'Saved successfully.', severity: 'success' });
    } else {
      setOptimisticText(null);
      setToast({ open: true, message: 'Failed to save — your change has been reverted.', severity: 'error' });
    }
  };

  const handleCancel = () => {
    setValue(displayText);
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleConfirm();
    }
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <>
      {saving ? (
        <span className={`inline-edit-saving ${className}`}>
          <span className="inline-edit-saving-text">{displayText}</span>
          <CircularProgress size={14} className="inline-edit-spinner" />
        </span>
      ) : editing ? (
        <span className={`inline-edit-active ${compact ? 'inline-edit-active--compact' : ''} ${className}`}>
          <TextField
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={compact ? handleConfirm : undefined}
            size="small"
            fullWidth
            multiline={!compact}
            inputRef={inputRef}
            variant="outlined"
            sx={
              compact
                ? {
                    fontSize: 'inherit',
                    '& .MuiInputBase-input': { padding: '2px 6px', fontSize: 'inherit' },
                    '& .MuiOutlinedInput-root': { padding: 0 },
                  }
                : { fontSize: 'inherit' }
            }
          />
          {!compact && (
            <span className="inline-edit-actions">
              <Tooltip title="Confirm (Enter)">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleConfirm}
                    disabled={!value.trim() || value.trim() === displayText}
                    className="inline-edit-confirm"
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Cancel (Esc)">
                <IconButton size="small" onClick={handleCancel} className="inline-edit-cancel">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </span>
          )}
        </span>
      ) : (
        <span className={`inline-edit-view ${className}`}>
          <span className="inline-edit-text">{displayText}</span>
          {!disabled && (
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => setEditing(true)} className="inline-edit-trigger">
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </span>
      )}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default InlineEditText;
